import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
} from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { getOSKeyLabel } from '../../../utility/os-detection';

@Component({
  selector: 'app-key-combo-button',
  standalone: true,
  imports: [ButtonModule, TooltipModule],
  templateUrl: './key-combo-button.component.html',
  styleUrl: './key-combo-button.component.scss',
})
export class KeyComboButtonComponent implements OnInit, OnDestroy {
  @Input() size: 'small' | 'large' | undefined;
  @Input() icon = '';
  @Input() outlined = false;
  @Input() text = false;
  @Input() styleClass = '';
  @Input() rounded = true;
  @Input() severity:
    | 'success'
    | 'info'
    | 'warning'
    | 'danger'
    | 'help'
    | 'primary'
    | 'secondary'
    | 'contrast'
    | null
    | undefined;
  @Input() label = '';
  @Input() keys: string[] = [];
  /** Shows the key combination in the button, otherwise only in the tooltip. */
  @Input() showKeyHint = true;
  @Input() tooltip = '';
  @Output() action = new EventEmitter<void>();

  private keyEventListener: (() => void) | undefined;
  keySuffix = '';
  tooltipText = '';

  constructor(private renderer: Renderer2) {}

  /**
   * Lifecycle hook that is called after the component's view has been fully initialized.
   * Executes initialization logic including binding key combinations and setting the key suffix.
   *
   * @return {void} This method does not return any value.
   */
  ngOnInit(): void {
    this.bindKeyCombo();
    this.setKeySuffix();
  }

  /**
   * Lifecycle hook that is called when the component is destroyed.
   * Performs cleanup tasks such as removing registered event listeners
   * to prevent memory leaks.
   *
   * @return {void} Does not return a value.
   */
  ngOnDestroy(): void {
    if (this.keyEventListener) {
      this.keyEventListener();
    }
  }

  /**
   * Binds a key combination event listener to the window object. The method listens
   * for a specific key combination (modifier key plus a secondary key) and executes
   * a handler if the combination is detected.
   *
   * @return {void} This function does not return a value.
   */
  private bindKeyCombo(): void {
    const modifier = this.keys[0];
    const key = this.keys[1];

    this.keyEventListener = this.renderer.listen(
      'window',
      'keydown',
      (event: KeyboardEvent) => {
        if (
          this.isModifierKeyActive(event, modifier) &&
          event.key.toLowerCase() === key
        ) {
          this.handleKeyboardEvent(event);
        }
      }
    );
  }

  /**
   * Determines if a specific modifier key is active during a keyboard event.
   *
   * @param {KeyboardEvent} event - The keyboard event to evaluate.
   * @param {string} modifier - The modifier key to check. Accepted values are 'control', 'alt', 'shift', and 'meta'.
   * @return {boolean} Returns true if the specified modifier key is active, otherwise false.
   */
  private isModifierKeyActive(event: KeyboardEvent, modifier: string): boolean {
    switch (modifier) {
      case 'control':
        return event.ctrlKey || event.metaKey; // MetaKey for Mac
      case 'alt':
        return event.altKey;
      case 'shift':
        return event.shiftKey;
      case 'meta':
        return event.metaKey;
      default:
        return false;
    }
  }

  /**
   * Constructs and sets the key suffix by combining the operating system-specific key label
   * and the uppercase version of the assigned key.
   *
   * @return {void} This method does not return a value.
   */
  private setKeySuffix(): void {
    const modifier = this.keys[0];
    const key = this.keys[1].toUpperCase();
    const osKeyLabel = getOSKeyLabel(modifier);

    this.keySuffix = `<kbd>${osKeyLabel}</kbd>+<kbd>${key}</kbd>`;
    this.tooltipText = this.tooltip
      ? `${this.tooltip} (${osKeyLabel} + ${key})`
      : `${osKeyLabel} + ${key}`;
  }

  /**
   * Handles the keyboard event and triggers the associated action.
   *
   * @param {KeyboardEvent} event - The keyboard event to handle.
   * @return {void} No return value.
   */
  handleKeyboardEvent(event: KeyboardEvent): void {
    this.action.emit();
  }

  /**
   * Triggers the execution of an action by emitting an event.
   * @return {void} Does not return a value.
   */
  buttonFunction(): void {
    this.action.emit();
  }
}
